import { useEffect, useState } from 'react';
import Header from '../../components/layouts/Header';
import { PageLayout } from '../../components/ui';
import { Card, CardBody, Button, Tab, Tabs } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useMediaQuery } from 'usehooks-ts';
import MarketTodayWidget from '../../components/dashboard/MarketTodayWidget';
import SeasonComparisonWidget from '../../components/dashboard/SeasonComparisonWidget';
import SeasonComplianceWidget from '../../components/dashboard/SeasonComplianceWidget';
import SettlementsWidget from '../../components/dashboard/SettlementsWidget';
import FOBMetricsWidget from '../../components/dashboard/FOBMetricsWidget';
import CIFEvolutionWidget from '../../components/dashboard/CIFEvolutionWidget';
import VSTAWidget from '../../components/dashboard/VSTAWidget';
import { logUserAction } from '../../services/actionsService';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { cn } from 'common/src/utils/cn';

type DashboardSection = 'market' | 'clients' | 'results' | 'comparisons';

const DashboardPage = () => {
  const { t } = useTranslation('dashboard');
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeSection, setActiveSection] = useState<DashboardSection>('market');

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const dashboardSections: Array<{
    key: DashboardSection;
    label: string;
    mobileLabel: string;
    icon: string;
  }> = [
    {
      key: 'market',
      label: t('Mercado'),
      mobileLabel: t('Mercado'),
      icon: 'solar:chart-2-linear',
    },
    {
      key: 'clients',
      label: t('Cumplimiento Clientes'),
      mobileLabel: t('Clientes'),
      icon: 'solar:users-group-rounded-linear',
    },
    {
      key: 'results',
      label: t('Resultados'),
      mobileLabel: t('Resultados'),
      icon: 'solar:wallet-money-linear',
    },
    {
      key: 'comparisons',
      label: t('Comparativos'),
      mobileLabel: t('Comparativos'),
      icon: 'solar:graph-new-up-linear',
    },
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'market':
        return (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Card>
              <CardBody className='p-0'>
                <MarketTodayWidget key={`market-${refreshKey}`} />
              </CardBody>
            </Card>

            <Card>
              <CardBody className='p-4'>
                <SeasonComparisonWidget key={`season-comparison-${refreshKey}`} />
              </CardBody>
            </Card>
          </div>
        );
      case 'clients':
        return (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Card className='h-[500px]'>
              <CardBody className='p-4 h-full'>
                <SeasonComplianceWidget key={`compliance-${refreshKey}`} />
              </CardBody>
            </Card>

            <Card className='h-[500px]'>
              <CardBody className='p-4 h-full'>
                <SettlementsWidget key={`settlements-${refreshKey}`} />
              </CardBody>
            </Card>
          </div>
        );
      case 'results':
        return <FOBMetricsWidget key={`fob-${refreshKey}`} />;
      case 'comparisons':
        return (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <CIFEvolutionWidget key={`cif-${refreshKey}`} />
            <VSTAWidget key={`vsta-${refreshKey}`} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout className='overflow-y-auto pb-28 md:pb-6'>
        {/** Track dashboard page visit */}
        <TrackDashboardView />
        <Header
          title={t('Dashboard')}
          description={isMobile ? undefined : t('¡Bienvenido al panel principal de la plataforma! Aquí puedes ver toda la información clave de un vistazo.')}
          actions={
            <Button
              color='primary'
              className='hidden md:flex'
              onPress={handleRefresh}
              startContent={<Icon icon='solar:refresh-linear' width={20} />}
            >
              {t('Actualizar')}
            </Button>
          }
        />
        <div className='fixed top-0 right-14 z-[60] p-4 md:hidden'>
          <Button
            isIconOnly
            aria-label={t('Actualizar')}
            className='shadow-md bg-background'
            onPress={handleRefresh}
          >
            <Icon className='text-default-500' icon='solar:refresh-linear' width={22} />
          </Button>
        </div>
        <div className='hidden md:flex sticky -top-6 z-30 -mx-6 px-6 py-2 bg-background/95 backdrop-blur border-b border-divider overflow-x-auto'>
          <Tabs
            aria-label={t('Dashboard')}
            variant='underlined'
            selectedKey={activeSection}
            onSelectionChange={(key) => setActiveSection(key as DashboardSection)}
          >
            {dashboardSections.map((section) => (
              <Tab
                key={section.key}
                title={
                  <div className='flex items-center gap-2'>
                    <Icon icon={section.icon} width={20} />
                    <span>{section.label}</span>
                  </div>
                }
              />
            ))}
          </Tabs>
        </div>

        <div className='mt-4 md:mt-6'>
          {renderActiveSection()}
        </div>

        <nav className='fixed inset-x-0 bottom-0 z-40 border-t border-divider bg-background/95 px-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-2 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur md:hidden'>
          <div className='grid grid-cols-4 gap-1'>
            {dashboardSections.map((section) => {
              const isActive = section.key === activeSection;

              return (
                <button
                  key={section.key}
                  type='button'
                  className={cn(
                    'flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg px-1 text-[11px] font-medium leading-tight transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-default-500 hover:bg-default-100'
                  )}
                  onClick={() => setActiveSection(section.key)}
                >
                  <Icon icon={section.icon} width={22} />
                  <span className='max-w-full truncate'>{section.mobileLabel}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </PageLayout>
    </div>
  );
};

export default DashboardPage;

// Small helper component to track dashboard visits once
const TrackDashboardView = () => {
  useEffect(() => {
    try {
      logUserAction('view', 'page', 'dashboard');
    } catch (e) {
      // no-op tracking failure
    }
  }, []);
  return null;
};
